import { useState } from "react"

/** useSteps is a simple hook for handling previous/next page. */
export const useSteps = <T>({
  maxSteps: _maxSteps,
  namedSteps,
  onStepChange,
  initialStep = 0,
}: {
  maxSteps?: number
  namedSteps?: T[]
  initialStep?: number | T
  onStepChange?: (
    nextStep: number,
    prevStep: number,
    nextStepName?: T,
    prevStepName?: T
  ) => void
}) => {
  const [step, setStep] = useState(
    Math.max(
      0,
      typeof initialStep === "number"
        ? initialStep
        : namedSteps
        ? namedSteps.findIndex((ns) => ns === initialStep)
        : 0 || 0
    )
  )
  const maxSteps =
    _maxSteps ?? (namedSteps ? (namedSteps?.length || -1) + 1 : 0)

  const onNext = () => {
    setStep((s) => {
      const n = Math.min(s + 1, maxSteps)
      if (s === n) {
        return s
      }
      const [ok, i, name] = getStepTuple(n)
      const [ok2, i2, name2] = getStepTuple(s)
      ok && ok2 && onStepChange?.(i, i2, name, name2)
      return n
    })
  }
  const onPrev = () => {
    setStep((s) => {
      const n = Math.max(0, s - 1)
      if (s === n) {
        return s
      }
      const [ok, i, name] = getStepTuple(n) || []
      const [ok2, i2, name2] = getStepTuple(s)
      ok && ok2 && onStepChange?.(i, i2, name, name2)
      return n
    })
  }
  function getStepTuple(step: (T) | number): [boolean, number, T] {
    if (typeof step === "number") {
      return [true, step, namedSteps?.[step]] as any
    }
    if (!step) {
      return [false, 0, ""] as any
    }
    if (!namedSteps) {
      return [false, 0, ""] as any
    }
    for (const [i, name] of namedSteps.entries()) {
      if (String(name) === String(step)) {
        return [true, i, name]
      }
    }
    return [false, 0, ""] as any
  }

  const hasNext = step < maxSteps - 2
  const hasPrevious = step >= 1
  return {
    step,
    stepName: namedSteps?.[step] as T,
    nextStepName: namedSteps?.[step + 1] as T,
    previousStepName: namedSteps?.[step - 1] as T,
    setStep: (step: number | T) => {
      const [ok, i, stepName] = getStepTuple(step)
      if (!ok) {
        return
      }
      setStep((s) => {
        if (s === i) {
          return s
        }
        const [ok2, i2, stepName2] = getStepTuple(s)

        ok2 && onStepChange?.(i, i2, stepName, stepName2)
        return i
      })
    },
    onNext,
    maxSteps,
    onPrev,
    hasNext,
    hasPrevious,
  }
}
